import { useCallback, useEffect, useRef } from 'react';

import { reaction } from 'mobx';

import { useFlag } from '../services/launchDarkly';
import { EnumKycStatus } from '../services/types';
import { EnumOpenDialog } from '../stores/ui/types';
import { useStores } from '../stores/useStores';
import type { MaybePromise } from '../utils/types';

export function useOpenOrderWithKYC<T>(handleOpenOrder: (data: T) => MaybePromise<void>) {
  const { payRedeemStore, uiStore, userStore } = useStores();

  const shouldCheckForceKyc = useFlag('should_check_force_kyc', true);

  const storePackageRef = useRef<T | null>(null);
  const isKycCheckInitiatedRef = useRef(false);
  const isKycCompleted = EnumKycStatus.completed === userStore.profile?.kycStatus;

  const handleOpenOrderRef = useRef(handleOpenOrder);
  handleOpenOrderRef.current = handleOpenOrder;

  const handleOpenOrderWithCheckForceKyc = useCallback(
    (data: T) => {
      if (shouldCheckForceKyc && !isKycCompleted) {
        storePackageRef.current = data;
        isKycCheckInitiatedRef.current = true;
        void payRedeemStore.handleCheckForceKYC();
      } else {
        void handleOpenOrder(data);
      }
    },
    [handleOpenOrder, isKycCompleted, payRedeemStore, shouldCheckForceKyc],
  );

  useEffect(() => {
    const dispose = reaction(
      () => ({
        isProcessForKYC: payRedeemStore.checkForceKYC.isProcessForKYC,
        isForceKYC: payRedeemStore.checkForceKYC.isForceKYC,
        isSubscription: payRedeemStore.checkForceKYC.isSubscription,
      }),
      ({ isProcessForKYC, isForceKYC, isSubscription }, prev) => {
        // Only react when processing completes (was true, now false) and this hook initiated the check
        if (
          shouldCheckForceKyc &&
          prev.isProcessForKYC &&
          !isProcessForKYC &&
          !isSubscription &&
          isKycCheckInitiatedRef.current
        ) {
          if (isForceKYC) {
            uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenKycDialog });
            return;
          }
          if (storePackageRef.current) {
            const data = storePackageRef.current;
            storePackageRef.current = null;
            isKycCheckInitiatedRef.current = false;
            void handleOpenOrderRef.current(data);
          }
        }
      },
    );

    return () => dispose();
  }, [shouldCheckForceKyc, payRedeemStore, uiStore]);

  return handleOpenOrderWithCheckForceKyc;
}
